import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import PageWrapper from '../components/PageWrapper';
import { Mail, Lock, User, Building, Phone, ArrowRight, AlertCircle, ArrowLeft } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { t } from '../data/translations';
import { authApi } from '../api/auth';
import { FieldError } from '../components/FieldError';

export default function RegisterPage() {
  usePageTitle("Créer un compte — Lodiss");

  const { lang, storeAuthData, setPage, clearError, apiError, isLoading } = useApp();
  const T = t[lang];

  const [form, setForm] = useState({
    full_name: '',
    company: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [success, setSuccess] = useState(false);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.full_name.trim()) {
      e.full_name = lang === 'fr' ? 'Ce champ est obligatoire' : 'This field is required';
    }
    if (!form.email.trim() || !form.email.includes('@')) {
      e.email = lang === 'fr' ? "L'adresse email n'est pas valide" : 'The email address is not valid';
    }
    if (!form.phone.trim()) {
      e.phone = lang === 'fr' ? 'Ce champ est obligatoire' : 'This field is required';
    }
    if (!form.phone.trim() || !/^[\d\s\-+()]+$/.test(form.phone)) {
      e.phone = lang === 'fr' ? 'Numéro de téléphone invalide' : 'Invalid phone number';
    }
    if (!form.password || form.password.length < 8) {
      e.password = lang === 'fr' ? 'Le mot de passe doit contenir au moins 8 caractères' : 'The password must be at least 8 characters';
    }
    if (form.password !== form.confirm_password) {
      e.confirm_password = lang === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match';
    }
    return e;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    try {
      const response = await authApi.register({
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        password: form.password,
        company: form.company || undefined,
      });

      storeAuthData(response);
      setPage('verify-email');

    } catch (err: any) {
      if (err.details) {
        const fieldErrors: Record<string, string> = {};
        if (typeof err.details === 'object') {
          Object.entries(err.details).forEach(([key, value]) => {
            fieldErrors[key] = Array.isArray(value) ? value[0] : String(value);
          });
        }
        setErrors(fieldErrors);
      } else if (err.fieldErrors) {
        setErrors(err.fieldErrors);
      } else {
        setErrors({ submit: err.message || (lang === 'fr' ? 'Erreur lors de l\'inscription' : 'Registration failed') });
      }
    }
  };

  const field = (key: keyof typeof form, label: string, icon: React.ReactNode, type = 'text', required = false) => (
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
        {label} {required && <span className="text-lodiss-red">*</span>}
      </label>
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          {icon}
        </div>
        <input
          type={type}
          value={form[key]}
          onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
          className={`w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-1 transition-colors ${
            errors[key]
              ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
              : 'border-gray-200 focus:border-lodiss-red focus:ring-lodiss-red/20'
          }`}
        />
      </div>
      <FieldError message={errors[key]} />
    </div>
  );

  return (
    <PageWrapper>
    <main className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-gray-950">
              {lang === 'fr' ? 'Créer un compte' : 'Create an account'}
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              {lang === 'fr' ? 'Rejoignez LODISS pour commander' : 'Join LODISS to order'}
            </p>
          </div>

          {errors.submit && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3">
              <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{errors.submit}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {field('full_name', lang === 'fr' ? 'Nom complet' : 'Full name', <User size={16} />, 'text', true)}
            {field('company', lang === 'fr' ? 'Entreprise (optionnel)' : 'Company (optional)', <Building size={16} />)}
            {field('email', 'Email', <Mail size={16} />, 'email', true)}
            {field('phone', lang === 'fr' ? 'Téléphone' : 'Phone', <Phone size={16} />, 'tel', true)}
            {field('password', lang === 'fr' ? 'Mot de passe' : 'Password', <Lock size={16} />, 'password', true)}
            {field('confirm_password', lang === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password', <Lock size={16} />, 'password', true)}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-lodiss-red hover:bg-lodiss-dark text-white font-semibold rounded-lg transition-colors duration-150 disabled:opacity-50 mt-4"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {lang === 'fr' ? 'Créer mon compte' : 'Create my account'}
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500">
              {lang === 'fr' ? 'Déjà un compte ?' : 'Already have an account?'}{' '}
              <button
                onClick={() => setPage('login')}
                className="text-lodiss-red font-semibold hover:underline"
              >
                {lang === 'fr' ? 'Se connecter' : 'Sign in'}
              </button>
            </p>
          </div>
        </div>
      </div>
    </main>
    </PageWrapper>
  );
}
